import React from 'react';
import { Box, Flex, DeprecatedIcon, Pane, Typography } from '@fullstory/components';
import { Bounds } from '../types';
import { InverseScales } from '../utils/scales';
import css from './index.module.scss';
import { FormattedMessage } from 'react-intl';

const TextColor = 'neutrals-n11';

export type ScrollMapsPageFoldMarkerVisibilityState =
  | 'visible'
  | 'offscreen_below'
  | 'offscreen_above';

export interface ScrollMapsPageFoldMarkerProps {
  /**
   * The inverse scales to be applied to undo any scaling applied by playback.
   */
  inverseScales: InverseScales;
  /**
   * The bounds of the Marker box itself.
   */
  markerBoxBounds: Bounds;
  /**
   * The median viewport height of users upon first loading the page (in px).
   */
  medianPageFold: number;
  /**
   * The current visibility state of the marker (is it on the screen, off the screen, etc.)
   */
  visibilityState: ScrollMapsPageFoldMarkerVisibilityState;
}

/**
 * The Scroll Maps Marker responsible for displaying page fold information
 */
export const ScrollMapsPageFoldMarker: React.FC<ScrollMapsPageFoldMarkerProps> = ({
  inverseScales,
  markerBoxBounds,
  medianPageFold,
  visibilityState,
}) => {
  // For now, always make the marker height be a fifth of the box height
  const markerBarHeight = (markerBoxBounds.height * inverseScales.y) / 5;

  // We only perform the css transform on the box itself instead of the bar (weird results are produced).
  // Hence, we need to modulate the thickness according to the inverse scale to set its height that way.
  return (
    <div data-component="ScrollMapsPageFoldMarker" data-fs="scroll-maps-page-fold-marker">
      <div
        data-fs="scroll-maps-page-fold-marker-body"
        data-visibility={visibilityState}
        style={{
          position: 'absolute',
          pointerEvents: 'none',
          transform: `translateX(0px) translateY(0px) scale(${inverseScales.x}, ${inverseScales.y})`,
          top: markerBoxBounds.top,
          left: markerBoxBounds.left,
          zIndex: markerBoxBounds.zIndex,
          opacity: visibilityState == 'visible' ? 1 : 0.8,
        }}
      >
        <Pane
          borderRadius={12}
          elevation={2}
          backgroundColor="default"
          width={markerBoxBounds.width}
          height={markerBoxBounds.height}
        >
          <Flex justifyContent="center" alignItems="center">
            {visibilityState != 'visible' && (
              <Box padding={[0, 4, 0, 4]}>
                <div className={css.topBuffer}>
                  <DeprecatedIcon
                    name={visibilityState == 'offscreen_above' ? 'arrow-up' : 'arrow-down'}
                    color="pink-dark"
                    size={12}
                  />
                </div>
              </Box>
            )}
            <Box padding={[0, 4, 0, 4]}>
              <div className={css.topBuffer}>
                <Typography variant="body3" color={TextColor}>
                  <FormattedMessage defaultMessage="Median Fold:" id="oKRE1U" />
                  &nbsp;
                </Typography>
                <Typography variant="heading5" color={TextColor}>
                  {medianPageFold}
                  <FormattedMessage defaultMessage="px" id="ui026m" />
                </Typography>
              </div>
            </Box>
          </Flex>
        </Pane>
      </div>
      {visibilityState === 'visible' && (
        <div
          style={{
            position: 'absolute',
            border: 'none',
            borderTop: `${markerBarHeight}px dotted #ffffff`,
            boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.1)',
            top: markerBoxBounds.top + markerBoxBounds.height / 2 - markerBarHeight / 2,
            width: '100%',
            zIndex: markerBoxBounds.zIndex - 1, // The box should be just above the bar.
          }}
        />
      )}
    </div>
  );
};
